import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Scale, Gavel, ArrowRight, Crown, LogIn, LogOut, User } from "lucide-react";
import { useSubscription } from "@/lib/subscription";
import { useAuth } from "@/lib/AuthContext";
import SignInModal from "@/components/SignInModal";

export default function Layout({ children }) {
  const { pathname } = useLocation();
  const sub = useSubscription();
  const { user, signedIn, signOut } = useAuth();
  const [signInOpen, setSignInOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const navItem = (to, label, testid) => {
    const active = pathname === to || (to !== "/" && pathname.startsWith(to));
    return (
      <Link
        to={to}
        data-testid={testid}
        className={`font-display uppercase text-sm tracking-widest px-3 py-1.5 rounded-full border-[3px] border-black transition-all ${
          active ? "bg-[#FFE600]" : "bg-white hover:bg-[#FFF9CC]"
        }`}
      >
        {label}
      </Link>
    );
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top alert strip */}
      <div className="pl-marquee" data-testid="marquee-banner">
        <div className="pl-marquee-track">
          {Array.from({ length: 2 }).map((_, i) => (
            <React.Fragment key={i}>
              <span>★ NOW HEARING: HE LEFT ME ON READ FOR 4 HOURS</span>
              <span>★ PETTY CRIMES WELCOME</span>
              <span>★ JUDGE POOKIE PRESIDING</span>
              <span>★ NOT REAL LEGAL ADVICE OBVIOUSLY</span>
              <span>★ FILE YOUR CASE FREE</span>
              <span>★ THE COURT IS UNHINGED BUT FAIR</span>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Nav */}
      <header className="sticky top-0 z-30 bg-[var(--pl-bg)] border-b-4 border-black">
        <nav className="max-w-7xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <span className="w-10 h-10 rounded-full bg-[#FF3B30] border-[3px] border-black grid place-items-center">
              <Scale className="text-white" strokeWidth={3} size={20} />
            </span>
            <span className="font-display text-2xl md:text-3xl tracking-tight">
              Pookie<span className="text-[#FF3B30]">Lawyer</span>
              <span className="text-black/40">.com</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-2">
            {navItem("/", "Court", "nav-home")}
            {navItem("/pricing", "Pricing", "nav-pricing")}
            {signedIn && navItem("/my-cases", "My Cases", "nav-my-cases")}
          </div>

          <div className="flex items-center gap-2">
            {sub.is_pro && (
              <span
                className="hidden sm:inline-flex pl-chip bg-[#B4F8C8] font-display"
                data-testid="nav-pro-badge"
              >
                <Crown size={12} strokeWidth={3} /> Pro
              </span>
            )}

            {signedIn ? (
              <div className="relative">
                <button
                  onClick={() => setProfileOpen((v) => !v)}
                  className="pl-btn pl-btn-ghost text-xs px-3 py-2"
                  data-testid="nav-profile"
                >
                  <User size={14} strokeWidth={3} />
                  <span className="hidden md:inline">{user?.phone}</span>
                </button>
                {profileOpen && (
                  <div
                    className="absolute right-0 mt-2 w-64 pl-card bg-white p-3 z-40"
                    onMouseLeave={() => setProfileOpen(false)}
                  >
                    <div className="px-2 pb-2 border-b-2 border-black/15">
                      <div className="pl-label">Signed in as</div>
                      <div className="font-mono-pl text-sm mt-1 truncate">{user?.phone}</div>
                    </div>
                    <Link
                      to="/my-cases"
                      onClick={() => setProfileOpen(false)}
                      className="block px-2 py-2 mt-2 hover:bg-[#FFE600] font-mono-pl text-sm rounded"
                      data-testid="profile-mycases"
                    >
                      My cases
                    </Link>
                    <button
                      onClick={() => {
                        signOut();
                        setProfileOpen(false);
                      }}
                      className="w-full text-left px-2 py-2 hover:bg-[#FFAEBC] font-mono-pl text-sm rounded flex items-center gap-2"
                      data-testid="profile-signout"
                    >
                      <LogOut size={14} strokeWidth={3} /> Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => setSignInOpen(true)}
                className="pl-btn pl-btn-ghost text-xs"
                data-testid="nav-signin"
              >
                <LogIn size={14} strokeWidth={3} />
                <span className="hidden sm:inline">Sign in</span>
              </button>
            )}

            <Link
              to="/file"
              data-testid="nav-cta-file"
              className="pl-btn pl-btn-primary text-xs md:text-sm"
            >
              <Gavel size={16} strokeWidth={3} />
              <span className="hidden sm:inline">File a Lawsuit</span>
              <ArrowRight size={16} strokeWidth={3} />
            </Link>
          </div>
        </nav>
      </header>

      <SignInModal open={signInOpen} onClose={() => setSignInOpen(false)} />

      <main className="flex-1">{children}</main>

      <footer className="mt-24 border-t-4 border-black bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-10 grid md:grid-cols-3 gap-6">
          <div>
            <div className="font-display text-2xl">PookieLawyer.com</div>
            <p className="font-mono-pl text-sm mt-2 text-black/70">
              The internet's most unserious court of cute drama. Not legal advice. Just vibes and verdicts.
            </p>
          </div>
          <div className="font-mono-pl text-sm">
            <div className="pl-label mb-2">Disclaimer</div>
            <p className="text-black/70">
              All cases, verdicts, and sentences are comedic fiction generated by AI. No actual pookies were prosecuted in the making of this website.
            </p>
          </div>
          <div className="font-mono-pl text-sm">
            <div className="pl-label mb-2">Court Hours</div>
            <p className="text-black/70">24/7 — even at 3am when you can't sleep over what they said.</p>
          </div>
        </div>
        <div className="border-t-4 border-black py-3 text-center font-mono-pl text-xs">
          © {new Date().getFullYear()} — Filed under: extremely silly.
        </div>
      </footer>
    </div>
  );
}
